import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, Search, Filter, Clock, ImageOff, User, Phone, FileText } from 'lucide-react';

const STATUS_COLORS = { levantada: '#3B82F6', en_proceso: '#F59E0B', turnada: '#8B5CF6', resuelta: '#10B981', cancelada: '#EF4444' };
const STATUS_BG = { levantada: '#DBEAFE', en_proceso: '#FEF3C7', turnada: '#EDE9FE', resuelta: '#D1FAE5', cancelada: '#FEE2E2' };
const STATUS_LABELS = { levantada: 'Levantada', en_proceso: 'En Proceso', turnada: 'Turnada a Alcaldía', resuelta: 'Resuelta', cancelada: 'Cancelada' };

function GestionCard({ gestion }) {
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const photo = gestion.fotos?.[0];
  const photoUrl = photo
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${photo.path}?auth=cookie`
    : null;
  const direccion = gestion.calle ? `${gestion.calle} ${gestion.numero || ''}${gestion.colonia ? ', ' + gestion.colonia : ''}` : 'Ubicación pendiente';

  return (
    <Link to={`/gestion/${gestion.id}`} data-testid={`gestion-item-${gestion.id}`} className="group block">
      <Card className="bg-white border border-[#E4E4E7] rounded-xl overflow-hidden hover:border-[#10B981] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer h-full">
        {/* Photo */}
        <div className="relative w-full aspect-[16/10] bg-gradient-to-br from-[#FAFAFA] to-[#F1F5F9] overflow-hidden">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={gestion.tipo}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-[#A1A1AA]">
              <ImageOff className="w-10 h-10 mb-1" />
              <span className="text-xs font-medium">Sin evidencia fotográfica</span>
            </div>
          )}
          <div className="absolute top-3 left-3">
            <Badge
              className="text-xs font-bold border-0 shadow-md"
              style={{ backgroundColor: STATUS_BG[gestion.status], color: STATUS_COLORS[gestion.status] }}
            >
              <span className="w-1.5 h-1.5 rounded-full mr-1.5" style={{ backgroundColor: STATUS_COLORS[gestion.status] }} />
              {STATUS_LABELS[gestion.status]}
            </Badge>
          </div>
          {gestion.seccion_electoral && (
            <div className="absolute top-3 right-3">
              <Badge className="text-xs bg-white/90 text-[#0A0A0A] border-0 shadow-md font-mono">
                Sec. {gestion.seccion_electoral}
              </Badge>
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
            <p className="text-white text-lg font-black tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {gestion.tipo === 'Otro' && gestion.tipo_otro ? gestion.tipo_otro : gestion.tipo}
            </p>
          </div>
        </div>

        {/* Content */}
        <CardContent className="p-4 space-y-2.5">
          <div className="flex items-start gap-2 text-sm text-[#0A0A0A]">
            <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#52525B] group-hover:text-[#10B981] transition-colors" />
            <span className="font-medium line-clamp-2">{direccion}</span>
          </div>

          {gestion.vecino_nombre && (
            <div className="flex items-center gap-2 text-sm text-[#52525B]">
              <User className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{gestion.vecino_nombre}</span>
              {gestion.vecino_telefono && (
                <>
                  <span className="text-[#E4E4E7]">·</span>
                  <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate text-xs">{gestion.vecino_telefono}</span>
                </>
              )}
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-[#F4F4F5]">
            <span className="text-xs text-[#52525B] flex items-center gap-1.5 font-medium">
              <Clock className="w-3.5 h-3.5" /> {formatDate(gestion.created_at)}
            </span>
            <span className="text-xs text-[#52525B] font-mono">
              #{gestion.id?.slice(0, 8)}
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function MisGestionesPage() {
  const { api } = useAuth();
  const [gestiones, setGestiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTipo, setFilterTipo] = useState('all');
  const [search, setSearch] = useState('');
  const [tipos, setTipos] = useState([]);

  const loadGestiones = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterTipo !== 'all') params.tipo = filterTipo;
      const res = await api.get('/gestiones', { params });
      setGestiones(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, filterStatus, filterTipo]);

  useEffect(() => {
    api.get('/tipos-gestion').then(res => setTipos(res.data)).catch(console.error);
  }, [api]);

  useEffect(() => {
    setLoading(true);
    loadGestiones();
  }, [loadGestiones]);

  const filtered = gestiones.filter(g => {
    if (!search) return true;
    const s = search.toLowerCase();
    return g.tipo?.toLowerCase().includes(s) ||
      g.calle?.toLowerCase().includes(s) ||
      g.colonia?.toLowerCase().includes(s) ||
      g.vecino_nombre?.toLowerCase().includes(s) ||
      g.seccion_electoral?.toLowerCase().includes(s);
  });

  const statusFilters = [
    { v: 'all', l: 'Todos' },
    { v: 'levantada', l: 'Levantadas' },
    { v: 'en_proceso', l: 'En Proceso' },
    { v: 'turnada', l: 'Turnadas' },
    { v: 'resuelta', l: 'Resueltas' },
    { v: 'cancelada', l: 'Canceladas' },
  ];

  return (
    <div className="space-y-6" data-testid="mis-gestiones-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Gestiones</h1>
        <p className="text-base text-[#52525B] mt-1">{filtered.length} de {gestiones.length} gestion{gestiones.length !== 1 ? 'es' : ''}</p>
      </div>

      {/* Search + Tipo */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
          <Input
            data-testid="search-input"
            className="h-12 pl-10 rounded-md border-[#E4E4E7] focus-visible:ring-[#10B981] focus-visible:border-[#10B981]"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por tipo, dirección, vecino o sección..."
          />
        </div>
        <Select value={filterTipo} onValueChange={setFilterTipo}>
          <SelectTrigger data-testid="filter-tipo" className="h-12 w-full sm:w-56 rounded-md border-[#E4E4E7]">
            <Filter className="w-4 h-4 mr-2 text-[#52525B]" />
            <SelectValue placeholder="Todos los tipos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {tipos.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Status pills */}
      <div className="flex flex-wrap gap-2" data-testid="status-pills">
        {statusFilters.map(s => {
          const active = filterStatus === s.v;
          return (
            <button
              key={s.v}
              data-testid={`status-pill-${s.v}`}
              onClick={() => setFilterStatus(s.v)}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-200 border ${
                active
                  ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]'
                  : 'bg-white text-[#52525B] border-[#E4E4E7] hover:bg-[#10B981] hover:text-white hover:border-[#10B981]'
              }`}
            >
              {s.l}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-8 h-8 border-2 border-[#0052FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-[#E4E4E7]">
          <CardContent className="py-16 text-center">
            <FileText className="w-12 h-12 text-[#E4E4E7] mx-auto mb-4" />
            <p className="text-lg font-semibold text-[#0A0A0A]">No hay gestiones</p>
            <p className="text-sm text-[#52525B]">Ajusta los filtros o crea un nuevo reporte</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gestiones-grid">
          {filtered.map(g => <GestionCard key={g.id} gestion={g} />)}
        </div>
      )}
    </div>
  );
}
